import * as React from "react";
import { format } from "date-fns";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import Loading from "@/components/Loading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/trpc";
import {
  CommunicationContentDialog,
  type CommunicationContentTarget,
} from "./admin-user-details/communication-content-dialog";
import { useAdminUserData } from "../hooks/useAdminUserData";

type AdminUserDetailsProps = {
  userId: string;
  context?: "page" | "dialog";
  onClose?: () => void;
};

type CandidateStatusFilter = "matches" | "rejected";

const DETAILS_CARD_CLASS_NAME =
  "min-w-0 overflow-hidden rounded-2xl border-slate-200 bg-white text-slate-900 shadow-xs";
const DETAILS_TABLE_CLASS_NAME =
  "text-sm [&_th]:bg-slate-50 [&_th]:px-4 [&_th]:text-slate-600 [&_td]:px-4 [&_td]:py-4 [&_tr]:border-slate-200";

const formatDateTime = (value?: Date | string | null) => {
  if (!value) {
    return "—";
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return format(parsed, "MMM d, yyyy h:mm a");
};

const formatDateOnly = (value?: Date | string | null) => {
  if (!value) {
    return "—";
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return format(parsed, "MMM d, yyyy");
};

const formatTimeOnly = (value?: Date | string | null) => {
  if (!value) {
    return "";
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return format(parsed, "h:mm a");
};

const StatusBadge = ({
  label,
  variant = "secondary",
}: {
  label: string;
  variant?: "secondary" | "destructive" | "warning" | "default" | "outline";
}) => (
  <Badge
    variant={variant}
    size="small"
    className={cn(
      "max-w-full whitespace-normal [overflow-wrap:anywhere]",
      (variant === "secondary" || variant === "outline") &&
        "border-slate-200 bg-slate-50 text-slate-700",
    )}
  >
    {label}
  </Badge>
);

const SectionHeader = ({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) => (
  <CardHeader className="flex flex-col gap-4 space-y-0 p-4 sm:p-5 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between">
    <div className="min-w-0 space-y-1 lg:flex-1">
      <CardTitle className="text-lg leading-6 text-slate-900">
        {title}
      </CardTitle>
      {description ? (
        <CardDescription className="text-sm leading-6 text-slate-600">
          {description}
        </CardDescription>
      ) : null}
    </div>
    {action}
  </CardHeader>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
    {message}
  </div>
);

export function AdminUserDetails({
  userId,
  context = "page",
  onClose,
}: AdminUserDetailsProps) {
  const {
    userQuery,
    communicationsQuery,
    interestedJobPostsQuery,
    recommendedJobPostsQuery,
  } = useAdminUserData(userId);
  const [candidateStatusFilter, setCandidateStatusFilter] =
    React.useState<CandidateStatusFilter>("matches");

  const [contentTarget, setContentTarget] =
    React.useState<CommunicationContentTarget | null>(null);

  // Delete user state
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [confirmName, setConfirmName] = React.useState("");
  const [deleteError, setDeleteError] = React.useState("");
  const deleteConfirmationId = React.useId();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const deleteUserMutation = useMutation(
    trpc.adminDashboard.deleteUser.mutationOptions({
      onSuccess: async () => {
        setShowDeleteDialog(false);
        setConfirmName("");
        setDeleteError("");
        await queryClient.invalidateQueries({
          queryKey: trpc.adminDashboard.getUsers.queryKey(),
        });
        onClose?.();
      },
      onError: (error) => {
        setDeleteError(
          error.message || "Failed to delete user. Please try again.",
        );
      },
    }),
  );

  const handleDeleteUser = () => {
    const user = userQuery.data;
    if (!user) {
      return;
    }

    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    if (confirmName.toLowerCase() !== fullName.toLowerCase()) {
      setDeleteError(`Please type "${fullName}" to confirm`);
      return;
    }

    setDeleteError("");
    deleteUserMutation.mutate({
      userId,
      confirmationName: confirmName,
    });
  };
  const matchesCount =
    recommendedJobPostsQuery.data?.filter((job) => job.isMatch).length ?? 0;
  const rejectedCount =
    recommendedJobPostsQuery.data?.filter((job) => !job.isMatch).length ?? 0;
  const recommendedJobs = React.useMemo(() => {
    if (!recommendedJobPostsQuery.data) {
      return [];
    }

    return candidateStatusFilter === "rejected"
      ? recommendedJobPostsQuery.data.filter((job) => !job.isMatch)
      : recommendedJobPostsQuery.data.filter((job) => job.isMatch);
  }, [candidateStatusFilter, recommendedJobPostsQuery.data]);

  if (userQuery.isLoading && !userQuery.data) {
    return (
      <div className="flex min-h-[50vh] min-w-0 items-center justify-center p-6">
        <Loading className="h-10 w-10 text-primary" />
      </div>
    );
  }

  if (userQuery.error || !userQuery.data) {
    return (
      <div
        className="m-4 flex min-w-0 flex-col items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-12 text-center"
        role="alert"
      >
        <AlertCircle aria-hidden="true" className="mb-2 size-8 text-red-600" />
        <p className="text-lg font-semibold text-slate-900">
          Failed to load user
        </p>
        <p className="text-sm text-slate-600 [overflow-wrap:anywhere]">
          {userQuery.error instanceof Error
            ? userQuery.error.message
            : "Something went wrong."}
        </p>
      </div>
    );
  }

  const user = userQuery.data;

  return (
    <div
      className={cn(
        "min-h-0 min-w-0 space-y-4 text-slate-700 sm:space-y-6",
        context === "dialog"
          ? "max-h-[85dvh] overflow-y-auto bg-slate-50 p-4 sm:p-6"
          : "pb-12",
      )}
    >
      <Card className={DETAILS_CARD_CLASS_NAME}>
        <CardHeader className="space-y-4 p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-3">
              <CardTitle className="text-2xl font-semibold leading-8 text-slate-900 [overflow-wrap:anywhere]">
                {user.firstName} {user.lastName}
              </CardTitle>
              <div className="flex min-w-0 flex-col gap-2 text-sm text-slate-600 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <span className="min-w-0 text-slate-700 [overflow-wrap:anywhere]">
                  {user.email}
                </span>
                <Separator
                  orientation="vertical"
                  className="hidden h-4 w-px bg-slate-200 sm:block"
                />
                <span className="text-xs text-slate-500 [overflow-wrap:anywhere]">
                  {user.phone ? user.phone : "No phone number"}
                </span>
                <Separator
                  orientation="vertical"
                  className="hidden h-4 w-px bg-slate-200 sm:block"
                />
                <span className="text-xs text-slate-500">
                  {user.devices.length > 0 ? "Push Enabled" : "Push Disabled"}
                </span>
                <Separator
                  orientation="vertical"
                  className="hidden h-4 w-px bg-slate-200 sm:block"
                />
                <span className="font-mono text-xs text-slate-500 [overflow-wrap:anywhere]">
                  {user.id}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  size="small"
                  className="max-w-full whitespace-normal border-slate-200 bg-slate-50 text-slate-600"
                >
                  Joined {formatDateTime(user.createdAt)}
                </Badge>
                <Badge
                  variant="outline"
                  size="small"
                  className="max-w-full whitespace-normal border-slate-200 bg-slate-50 text-slate-600"
                >
                  Last active: {formatDateTime(user.lastActiveAt)}
                </Badge>
                <Badge
                  variant="outline"
                  size="small"
                  className="max-w-full whitespace-normal border-slate-200 bg-slate-50 text-slate-600 [overflow-wrap:anywhere]"
                >
                  Last platform: {user.lastPlatform}
                </Badge>
                <Badge
                  variant="outline"
                  size="small"
                  className="max-w-full whitespace-normal border-slate-200 bg-slate-50 text-slate-600 [overflow-wrap:anywhere]"
                >
                  Timezone: {user.timezoneId}
                </Badge>
              </div>
            </div>
            <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
              <Button
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete User
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className={DETAILS_CARD_CLASS_NAME}>
        <SectionHeader
          title="Communications"
          description="Recent email, push, or SMS logs. Sorted by most recent first."
          action={
            <Button
              variant="outline"
              className="w-full lg:w-auto"
              onClick={() => communicationsQuery.refetch()}
              disabled={communicationsQuery.isFetching}
            >
              {communicationsQuery.isFetching ? "Refreshing..." : "Refresh"}
            </Button>
          }
        />
        <CardContent className="min-w-0 px-4 pb-4 pt-0 sm:px-5 sm:pb-5">
          {communicationsQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loading className="h-8 w-8 text-primary" />
            </div>
          ) : communicationsQuery.error ? (
            <div
              className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              role="alert"
            >
              Failed to load communications.
            </div>
          ) : communicationsQuery.data?.length ? (
            <div
              className="min-w-0 overflow-x-auto rounded-xl border border-slate-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              role="region"
              aria-label="Communications"
              tabIndex={0}
            >
              <Table className={cn(DETAILS_TABLE_CLASS_NAME, "min-w-[1000px]")}>
                <TableHeader>
                  <TableRow className="text-xs font-medium hover:bg-slate-50">
                    <TableHead className="w-[12%]">Sent</TableHead>
                    <TableHead className="w-[12%]">Method</TableHead>
                    <TableHead className="w-[14%]">Category</TableHead>
                    <TableHead className="w-[12%]">Status</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[6%] text-right">Opens</TableHead>
                    <TableHead className="w-[12%]">Last opened</TableHead>
                    <TableHead className="w-[12%]">Link clicked</TableHead>
                    <TableHead className="w-px">
                      <span className="sr-only">Content</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {communicationsQuery.data.map((comm) => {
                    const statusVariant =
                      comm.status === "failed"
                        ? "destructive"
                        : comm.status === "delivered" || comm.status === "sent"
                          ? "secondary"
                          : "outline";
                    const description =
                      comm.description ||
                      comm.templateId ||
                      comm.failedReason ||
                      "—";

                    return (
                      <TableRow
                        key={comm.id}
                        className="align-top text-sm hover:bg-slate-50/70"
                      >
                        <TableCell className="whitespace-nowrap">
                          <div className="font-medium">
                            {formatDateOnly(comm.sentAt)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatTimeOnly(comm.sentAt)}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap capitalize">
                          {comm.communication_method ?? "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap capitalize">
                          {comm.communicationCategory ?? "—"}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            label={comm.status}
                            variant={
                              statusVariant as
                                | "secondary"
                                | "destructive"
                                | "warning"
                                | "default"
                                | "outline"
                            }
                          />
                        </TableCell>
                        <TableCell className="max-w-[320px] text-sm">
                          <p className="line-clamp-2 [overflow-wrap:anywhere]">
                            {description}
                          </p>
                        </TableCell>
                        <TableCell className="text-right text-xs text-slate-500">
                          {comm.opened_count ?? 0}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-slate-500">
                          {formatDateTime(comm.lastOpenedAt)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-slate-500">
                          {formatDateTime(comm.linkClickedAt)}
                        </TableCell>
                        <TableCell>
                          {comm.hasContent ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setContentTarget({
                                  notificationDeliveryId: comm.id,
                                  method: comm.communication_method ?? null,
                                  category: comm.communicationCategory ?? null,
                                  sentAtLabel: formatDateTime(comm.sentAt),
                                })
                              }
                            >
                              View
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState message="No communications found." />
          )}
        </CardContent>
      </Card>

      <Card className={DETAILS_CARD_CLASS_NAME}>
        <SectionHeader
          title="Interested job posts"
          description="Job posts the user marked as interested."
          action={
            <Button
              variant="outline"
              className="w-full lg:w-auto"
              onClick={() => interestedJobPostsQuery.refetch()}
              disabled={interestedJobPostsQuery.isFetching}
            >
              {interestedJobPostsQuery.isFetching ? "Refreshing..." : "Refresh"}
            </Button>
          }
        />
        <CardContent className="min-w-0 px-4 pb-4 pt-0 sm:px-5 sm:pb-5">
          {interestedJobPostsQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loading className="h-8 w-8 text-primary" />
            </div>
          ) : interestedJobPostsQuery.error ? (
            <div
              className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              role="alert"
            >
              Failed to load interested job posts.
            </div>
          ) : interestedJobPostsQuery.data?.length ? (
            <div
              className="min-w-0 overflow-x-auto rounded-xl border border-slate-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              role="region"
              aria-label="Interested job posts"
              tabIndex={0}
            >
              <Table className={cn(DETAILS_TABLE_CLASS_NAME, "min-w-[760px]")}>
                <TableHeader>
                  <TableRow className="hover:bg-slate-50">
                    <TableHead>Title</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Business unit</TableHead>
                    <TableHead>Interested at</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {interestedJobPostsQuery.data.map((job) => (
                    <TableRow
                      key={job.decisionId}
                      className="hover:bg-slate-50/70"
                    >
                      <TableCell className="max-w-64 font-medium text-slate-900 [overflow-wrap:anywhere]">
                        {job.title}
                      </TableCell>
                      <TableCell className="max-w-48 [overflow-wrap:anywhere]">
                        {job.organizationName ?? "—"}
                      </TableCell>
                      <TableCell className="max-w-48 [overflow-wrap:anywhere]">
                        {job.organizationBusinessUnitName ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-slate-500">
                        {formatDateTime(job.decisionCreatedAt)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          label={job.open ? "Open" : "Closed"}
                          variant={job.open ? "secondary" : "destructive"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState message="No interested job posts found." />
          )}
        </CardContent>
      </Card>

      <Card className={DETAILS_CARD_CLASS_NAME}>
        <SectionHeader
          title="Recommended job posts"
          description="Job posts recommended to the user."
          action={
            <div className="flex min-w-0 max-w-full flex-col gap-3 xl:flex-row xl:items-center xl:justify-end">
              <Tabs
                value={candidateStatusFilter}
                onValueChange={(value) =>
                  setCandidateStatusFilter(value as CandidateStatusFilter)
                }
                className="min-w-0"
              >
                <TabsList
                  className="grid h-auto min-h-12 w-full min-w-0 grid-cols-2 rounded-xl border border-slate-200 bg-slate-100 p-1"
                  aria-label="Recommended job status"
                >
                  <TabsTrigger
                    value="matches"
                    className="h-full min-h-9 min-w-0 whitespace-normal rounded-lg px-2 py-2 text-sm font-medium text-slate-600 focus-visible:ring-primary focus-visible:ring-offset-0 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-xs"
                  >
                    Matches ({matchesCount})
                  </TabsTrigger>
                  <TabsTrigger
                    value="rejected"
                    className="h-full min-h-9 min-w-0 whitespace-normal rounded-lg px-2 py-2 text-sm font-medium text-slate-600 focus-visible:ring-primary focus-visible:ring-offset-0 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-xs"
                  >
                    Rejected ({rejectedCount})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Button
                variant="outline"
                className="w-full xl:w-auto"
                onClick={() => recommendedJobPostsQuery.refetch()}
                disabled={recommendedJobPostsQuery.isFetching}
              >
                {recommendedJobPostsQuery.isFetching
                  ? "Refreshing..."
                  : "Refresh"}
              </Button>
            </div>
          }
        />
        <CardContent className="min-w-0 px-4 pb-4 pt-0 sm:px-5 sm:pb-5">
          {recommendedJobPostsQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loading className="h-8 w-8 text-primary" />
            </div>
          ) : recommendedJobPostsQuery.error ? (
            <div
              className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              role="alert"
            >
              Failed to load recommended job posts.
            </div>
          ) : recommendedJobs.length ? (
            <div
              className="min-w-0 overflow-x-auto rounded-xl border border-slate-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              role="region"
              aria-label="Recommended job posts"
              tabIndex={0}
            >
              <Table className={cn(DETAILS_TABLE_CLASS_NAME, "min-w-[900px]")}>
                <TableHeader>
                  <TableRow className="hover:bg-slate-50">
                    <TableHead>Job</TableHead>
                    <TableHead>Provider Decision</TableHead>
                    <TableHead>Evaluation</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recommendedJobs.map((job) => (
                    <TableRow key={job.id} className="hover:bg-slate-50/70">
                      <TableCell className="max-w-64 font-medium [overflow-wrap:anywhere]">
                        <Link
                          to="/admin/job-posts/$jobPostId"
                          params={{ jobPostId: job.jobPostId }}
                          className="rounded-sm text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        >
                          {job.title}
                        </Link>
                        <div className="mt-1 text-sm text-slate-500">
                          {job.organizationName ?? "—"}
                          {job.organizationBusinessUnitName
                            ? ` • ${job.organizationBusinessUnitName}`
                            : ""}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[260px] space-y-1 text-sm [overflow-wrap:anywhere]">
                        {job.userDecision ? (
                          <>
                            <div className="font-medium">
                              {job.userDecision.interested
                                ? "Interested"
                                : "Not interested"}
                            </div>
                            {!job.userDecision.interested ? (
                              <div className="space-y-1 text-xs text-slate-500">
                                {job.userDecisionReasons?.length ? (
                                  <div>
                                    Reasons:{" "}
                                    {job.userDecisionReasons
                                      .map((reason) => reason?.name)
                                      .filter(Boolean)
                                      .join(", ")}
                                  </div>
                                ) : null}
                                {job.userDecision.description ? (
                                  <div>
                                    Description: {job.userDecision.description}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-slate-500">
                            {job.isMatch
                              ? "Not decided"
                              : "Not shown to provider"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          label={job.isMatch ? "Potential" : "Rejected"}
                          variant="secondary"
                        />
                      </TableCell>
                      <TableCell className="max-w-40 [overflow-wrap:anywhere]">
                        {job.isMatchChangedBy ?? "—"}
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        {job.aiEvaluationReason || job.userEvaluationReason ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="w-full truncate rounded-sm text-left underline decoration-slate-400 decoration-dotted underline-offset-4 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                              >
                                {job.aiEvaluationReason ||
                                  job.userEvaluationReason}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="max-w-[calc(100vw-2rem)] whitespace-pre-wrap rounded-xl border-slate-200 bg-white text-sm leading-6 text-slate-700 [overflow-wrap:anywhere] shadow-lg">
                              {job.aiEvaluationReason ||
                                job.userEvaluationReason}
                            </PopoverContent>
                          </Popover>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState message="No recommended job posts found." />
          )}
        </CardContent>
      </Card>

      <CommunicationContentDialog
        userId={userId}
        target={contentTarget}
        onClose={() => setContentTarget(null)}
      />

      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) {
            setConfirmName("");
            setDeleteError("");
          }
        }}
      >
        <DialogContent className="!w-[calc(100vw-2rem)] !max-w-lg !max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
          <DialogHeader className="pr-6 text-left">
            <DialogTitle className="text-xl leading-7">
              Delete User Account
            </DialogTitle>
            <DialogDescription className="text-sm leading-6 text-slate-600">
              This action cannot be undone. This will permanently delete the
              user's account and remove their access to all services.
            </DialogDescription>
          </DialogHeader>

          <div className="min-w-0 space-y-5">
            <Alert className="rounded-xl border-slate-200 bg-slate-50 text-slate-700 [&>svg]:text-slate-500">
              <AlertCircle aria-hidden="true" className="h-4 w-4" />
              <AlertTitle className="text-sm leading-5 text-slate-900">
                Data Retention Notice
              </AlertTitle>
              <AlertDescription className="text-sm leading-6">
                While the account will be deleted, certain information is
                legally required to be retained:
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  <li>Audit trails of account activity</li>
                  <li>Transaction records and payment history</li>
                  <li>Other data required by regulatory compliance</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="grid min-w-0 gap-2">
              <Label
                htmlFor={deleteConfirmationId}
                className="[overflow-wrap:anywhere]"
              >
                Please type{" "}
                <span className="font-medium">
                  {user.firstName} {user.lastName}
                </span>{" "}
                to confirm:
              </Label>
              <Input
                id={deleteConfirmationId}
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                aria-describedby={
                  deleteError ? `${deleteConfirmationId}-error` : undefined
                }
                placeholder={`${user.firstName} ${user.lastName}`}
              />
              {deleteError && (
                <p
                  id={`${deleteConfirmationId}-error`}
                  className="text-sm text-red-700 [overflow-wrap:anywhere]"
                  role="alert"
                >
                  {deleteError}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-0 flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:space-x-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              className="w-full sm:w-auto"
              disabled={deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
